import { MenuItem } from "@components";
import {
  BackwardThin,
  BadgeDollarRegular,
  BuildingsThin,
  ChartMixedRegular,
  ClockLight,
  CreditCardLight,
  EnvelopeRegular,
  FileInvoiceLight,
  LineColumnsRegular,
  PenFieldRegular,
  PuzzleLightFull,
  RepeatLight,
  ToolboxLight,
  UserRegular,
} from "@icons";
import { useLocalstorage } from "@src/hooks/helperHooks";
import RequirePermission from "@src/modules/member/components/require-permission";
import { LAST_APP_PAGE_VISITED_KEY } from "@src/modules/member/constants";
import { PERMISSIONS } from "@src/modules/member/constants/permissions";
import { useOrganizationHasFeature } from "@src/modules/member/hooks/organizations";
import { useMemberPath } from "@src/modules/member/hooks/use-member-path";
import { FeatureSlug } from "@src/modules/member/models/organization";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { DASHBOARD_PATH } from "../../../main/router/paths";
import {
  ACTIVITY_PATH,
  BILLING_PATH,
  CUSTOMER_EMAIL_PATH,
  CUSTOM_FIELDS_LIST_PATH,
  EXPENSES_INBOUND_PATH,
  EXPENSE_BUDGETS_PATH,
  EXPENSE_CATEGORIES_PATH,
  INTEGRATIONS,
  INVOICE_SETTINGS_PATH,
  ORGANIZATION_PATH,
  PROFILE_PATH,
  REPORT_SUBSCRIPTIONS_PATH,
  SERVICES_LIST_PATH,
  TIME_ENTRIES_SETTINGS_PATH,
} from "../paths";

interface MenuItemComponentPropTypes {
  minimized: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}

const memberSettingsMenuList = [
  {
    key: "back",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const navigate = useNavigate();
      const location = useLocation();
      const { path } = useMemberPath();
      const dashboardPath = path(DASHBOARD_PATH());
      const [lastAppPageVisited] = useLocalstorage(
        LAST_APP_PAGE_VISITED_KEY,
        dashboardPath,
      );

      const handleBack = () => {
        const target = lastAppPageVisited as string;
        if (!target || target === location.pathname) {
          navigate(dashboardPath);
        } else {
          navigate(target);
        }
      };

      return (
        <button
          className={classNames("flex gap-2 items-center px-2 py-2")}
          onClick={handleBack}
        >
          <span className={classNames({ "mr-2": !props.minimized })}>
            <BackwardThin />
          </span>
          {!props.minimized && <span>{t("member.settings.back")}</span>}
        </button>
      );
    },
  },
  {
    key: "divider-1",
    component: () => {
      return <div className="w-full h-[1px] bg-gray-200 my-2" />;
    },
  },
  {
    key: "profile-settings",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { settingsPath } = useMemberPath();

      return (
        <MenuItem
          to={settingsPath(PROFILE_PATH())}
          icon={
            <div
              className={classNames(
                "md:text-gray-500",
                { "text-gray-500": props.minimized },
                { "text-white": !props.minimized },
              )}
            >
              <UserRegular width={13} />
            </div>
          }
          label={t("member.settings.profile.profile")}
          minimized={props.minimized}
          setMobileMenuOpen={props.setMobileMenuOpen}
        />
      );
    },
  },
  {
    key: "organization-settings",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { settingsPath } = useMemberPath();

      return (
        <RequirePermission permission={PERMISSIONS.ORGANIZATION_UPDATE}>
          <MenuItem
            to={settingsPath(ORGANIZATION_PATH())}
            icon={
              <div
                className={classNames(
                  "md:text-gray-500",
                  { "text-gray-500": props.minimized },
                  { "text-white": !props.minimized },
                )}
              >
                <BuildingsThin width={14} />
              </div>
            }
            label={t("member.settings.organization.organization")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
  {
    key: "divider-2",
    component: () => {
      return (
        <RequirePermission permission={PERMISSIONS.ORGANIZATION_UPDATE}>
          <div className="w-full h-[1px] bg-gray-200 my-2" />
        </RequirePermission>
      );
    },
  },
  {
    key: "invoice-settings",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { settingsPath } = useMemberPath();

      return (
        <RequirePermission permission={PERMISSIONS.INVOICES_VIEW_LIST}>
          <MenuItem
            to={settingsPath(INVOICE_SETTINGS_PATH())}
            icon={
              <div
                className={classNames(
                  "md:text-gray-500",
                  { "text-gray-500": props.minimized },
                  { "text-white": !props.minimized },
                )}
              >
                <FileInvoiceLight width={13} />
              </div>
            }
            label={t("member.settings.invoices.invoice")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
  {
    key: "services-settings",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { settingsPath } = useMemberPath();

      return (
        <RequirePermission permission={PERMISSIONS.SERVICES_CREATE}>
          <MenuItem
            to={settingsPath(SERVICES_LIST_PATH())}
            icon={
              <div
                className={classNames(
                  "md:text-gray-500",
                  { "text-gray-500": props.minimized },
                  { "text-white": !props.minimized },
                )}
              >
                <ToolboxLight width={14} />
              </div>
            }
            label={t("member.settings.services.services")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
  {
    key: "time-entries-settings",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { settingsPath } = useMemberPath();

      return (
        <RequirePermission permission={PERMISSIONS.ORGANIZATION_UPDATE}>
          <MenuItem
            to={settingsPath(TIME_ENTRIES_SETTINGS_PATH())}
            icon={
              <div
                className={classNames(
                  "md:text-gray-500",
                  { "text-gray-500": props.minimized },
                  { "text-white": !props.minimized },
                )}
              >
                <ClockLight width={14} />
              </div>
            }
            label={t("member.settings.timeEntries.timeEntries")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
  {
    key: "custom-fields",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { settingsPath } = useMemberPath();

      return (
        <RequirePermission permission={PERMISSIONS.CUSTOM_FIELDS_VIEW_LIST}>
          <MenuItem
            to={settingsPath(CUSTOM_FIELDS_LIST_PATH())}
            icon={
              <div
                className={classNames(
                  "md:text-gray-500",
                  { "text-gray-500": props.minimized },
                  { "text-white": !props.minimized },
                )}
              >
                <PenFieldRegular />
              </div>
            }
            label={t("member.settings.customFields.customFields")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
  {
    key: "customer-email",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { settingsPath } = useMemberPath();

      return (
        <RequirePermission
          any={[
            PERMISSIONS.CONTACT_TYPES_VIEW_LIST,
            PERMISSIONS.ADVANCED_ORG_SETTINGS_VIEW,
          ]}
        >
          <MenuItem
            to={settingsPath(CUSTOMER_EMAIL_PATH())}
            icon={
              <div
                className={classNames(
                  "md:text-gray-500",
                  { "text-gray-500": props.minimized },
                  { "text-white": !props.minimized },
                )}
              >
                <EnvelopeRegular width={14} />
              </div>
            }
            label={t("member.settings.customerEmail.pageTitle")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
  {
    key: "expense-divider",
    component: () => {
      return (
        <RequirePermission permission={PERMISSIONS.EXPENSES_MANAGE}>
          <div className="w-full h-[1px] bg-gray-200 my-2" />
        </RequirePermission>
      );
    },
  },
  {
    key: "expense-categories",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { settingsPath } = useMemberPath();

      return (
        <RequirePermission permission={PERMISSIONS.EXPENSES_MANAGE}>
          <MenuItem
            to={settingsPath(EXPENSE_CATEGORIES_PATH())}
            icon={
              <div
                className={classNames(
                  "md:text-gray-500",
                  { "text-gray-500": props.minimized },
                  { "text-white": !props.minimized },
                )}
              >
                <BadgeDollarRegular width={14} />
              </div>
            }
            label={t("member.settings.expenseCategories.pageTitle")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
  {
    key: "expense-budgets",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { settingsPath } = useMemberPath();

      return (
        <RequirePermission permission={PERMISSIONS.EXPENSES_MANAGE}>
          <MenuItem
            to={settingsPath(EXPENSE_BUDGETS_PATH())}
            icon={
              <div
                className={classNames(
                  "md:text-gray-500",
                  { "text-gray-500": props.minimized },
                  { "text-white": !props.minimized },
                )}
              >
                <ChartMixedRegular width={14} />
              </div>
            }
            label={t("member.settings.expenseBudgets.pageTitle")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
  {
    key: "expenses-inbound",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { settingsPath } = useMemberPath();
      const hasFeature = useOrganizationHasFeature(
        FeatureSlug.ExpenseInboundEmail,
      );

      if (!hasFeature) return null;

      return (
        <RequirePermission permission={PERMISSIONS.EXPENSES_MANAGE}>
          <MenuItem
            to={settingsPath(EXPENSES_INBOUND_PATH())}
            icon={
              <div
                className={classNames(
                  "md:text-gray-500",
                  { "text-gray-500": props.minimized },
                  { "text-white": !props.minimized },
                )}
              >
                <EnvelopeRegular width={14} />
              </div>
            }
            label={t("member.settings.expensesInbound.menuLabel")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
  {
    key: "billing-divider",
    component: () => {
      return (
        <RequirePermission permission={PERMISSIONS.BILLING_VIEW}>
          <div className="w-full h-[1px] bg-gray-200 my-2" />
        </RequirePermission>
      );
    },
  },
  {
    key: "billing",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { settingsPath } = useMemberPath();

      return (
        <RequirePermission permission={PERMISSIONS.BILLING_VIEW}>
          <MenuItem
            to={settingsPath(BILLING_PATH())}
            icon={
              <div
                className={classNames(
                  "md:text-gray-500",
                  { "text-gray-500": props.minimized },
                  { "text-white": !props.minimized },
                )}
              >
                <CreditCardLight width={14} />
              </div>
            }
            label={t("member.settings.billing.billing")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
  {
    key: "activity",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { settingsPath } = useMemberPath();

      return (
        <RequirePermission permission={PERMISSIONS.AUDIT_LOGS_VIEW}>
          <MenuItem
            to={settingsPath(ACTIVITY_PATH())}
            icon={
              <div
                className={classNames(
                  "md:text-gray-500",
                  { "text-gray-500": props.minimized },
                  { "text-white": !props.minimized },
                )}
              >
                <LineColumnsRegular width={14} />
              </div>
            }
            label={t("activity.title")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
  {
    key: "divider-4",
    component: () => {
      return (
        <RequirePermission permission={PERMISSIONS.REPORT_SUBSCRIPTIONS_VIEW}>
          <div className="w-full h-[1px] bg-gray-200 my-2" />
        </RequirePermission>
      );
    },
  },
  {
    key: "report-subscriptions",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { settingsPath } = useMemberPath();

      return (
        <RequirePermission permission={PERMISSIONS.REPORT_SUBSCRIPTIONS_VIEW}>
          <MenuItem
            to={settingsPath(REPORT_SUBSCRIPTIONS_PATH())}
            icon={
              <div
                className={classNames(
                  "md:text-gray-500",
                  { "text-gray-500": props.minimized },
                  { "text-white": !props.minimized },
                )}
              >
                <RepeatLight width={14} />
              </div>
            }
            label={t("member.settings.reportSubscriptions.title")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
  {
    key: "divider-5",
    component: () => {
      return (
        <RequirePermission permission={PERMISSIONS.INTEGRATIONS_VIEW}>
          <div className="w-full h-[1px] bg-gray-200 my-2" />
        </RequirePermission>
      );
    },
  },
  {
    key: "integrations",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { settingsPath } = useMemberPath();

      return (
        <RequirePermission permission={PERMISSIONS.INTEGRATIONS_VIEW}>
          <MenuItem
            to={settingsPath(INTEGRATIONS())}
            icon={
              <div
                className={classNames(
                  "md:text-gray-500",
                  { "text-gray-500": props.minimized },
                  { "text-white": !props.minimized },
                )}
              >
                <PuzzleLightFull width={20} />
              </div>
            }
            label={t("member.settings.integrations.integrations")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
];

export default memberSettingsMenuList;
